export const DIAGRAM_FORMATS = Object.freeze({
    NONE: 'none',
    SVG: 'svg',
    EXCALIDRAW: 'excalidraw',
});

const VALID_DIAGRAM_FORMATS = new Set(Object.values(DIAGRAM_FORMATS));
const SUPPORTED_EXCALIDRAW_ELEMENT_TYPES = new Set([
    'rectangle',
    'ellipse',
    'diamond',
    'line',
    'arrow',
    'freedraw',
    'text',
    'image',
    'frame',
]);

// Diagram payloads now support both legacy SVG markup and native Excalidraw scenes.
const hasNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
// Older imports stored the words "null" / "undefined" as diagram text (204 prod questions,
// 2026-10-24), which drew a diagram reading "null". Treat those as no diagram.
const isPlaceholderString = (value) => typeof value === 'string' && /^(null|undefined)$/i.test(value.trim());
const hasDiagramText = (value) => hasNonEmptyString(value) && !isPlaceholderString(value);

const isPlainObject = (value) => (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value)
);
const isFiniteNumber = (value) => Number.isFinite(Number(value));

const deepCloneJsonValue = (value) => JSON.parse(JSON.stringify(value));

const sanitizeStoredExcalidrawAppState = (appState = {}) => {
    if (!isPlainObject(appState)) {
        return {};
    }

    // Excalidraw keeps collaborators as a runtime Map. Persisting it through JSON turns it into
    // a plain object and crashes the next mount, so we always strip it from stored scenes.
    const sanitizedAppState = deepCloneJsonValue(appState);
    delete sanitizedAppState.collaborators;
    return sanitizedAppState;
};

const buildRuntimeExcalidrawAppState = (appState = {}) => ({
    viewBackgroundColor: '#ffffff',
    ...sanitizeStoredExcalidrawAppState(appState),
    // Excalidraw expects collaborators to behave like a Map at runtime.
    collaborators: new Map(),
});

export const normalizeDiagramFormat = (value) => {
    if (value == null || value === '') {
        return null;
    }

    const normalizedValue = String(value).trim().toLowerCase();
    return VALID_DIAGRAM_FORMATS.has(normalizedValue) ? normalizedValue : null;
};

export const createEmptyExcalidrawScene = () => ({
    type: 'excalidraw',
    version: 2,
    source: 'learnerapp',
    elements: [],
    appState: buildRuntimeExcalidrawAppState(),
    files: {},
});

const normalizeExcalidrawPoints = (points, baseX = 0, baseY = 0) => {
    if (!Array.isArray(points)) {
        return null;
    }

    const normalizedPoints = [];
    for (const point of points) {
        if (Array.isArray(point) && point.length >= 2 && isFiniteNumber(point[0]) && isFiniteNumber(point[1])) {
            normalizedPoints.push([Number(point[0]), Number(point[1])]);
            continue;
        }
        if (isPlainObject(point) && isFiniteNumber(point.x) && isFiniteNumber(point.y)) {
            normalizedPoints.push([Number(point.x) - baseX, Number(point.y) - baseY]);
            continue;
        }
        return null;
    }

    return normalizedPoints;
};

const normalizeSceneElement = (rawElement, index) => {
    if (!isPlainObject(rawElement)) {
        return null;
    }

    // Some generated payloads invent non-Excalidraw types such as path/arc/sector.
    // We salvage simple path-like payloads as line elements and drop the rest.
    let nextType = String(rawElement.type || '').trim().toLowerCase();
    if (nextType === 'path') {
        nextType = 'line';
    }
    if (!SUPPORTED_EXCALIDRAW_ELEMENT_TYPES.has(nextType)) {
        return null;
    }

    const element = deepCloneJsonValue(rawElement);
    element.type = nextType;
    element.id = hasNonEmptyString(element.id) ? element.id : `generated-${index}`;
    element.x = isFiniteNumber(element.x)
        ? Number(element.x)
        : (isFiniteNumber(element.cx) && isFiniteNumber(element.rx) ? Number(element.cx) - Number(element.rx) : 0);
    element.y = isFiniteNumber(element.y)
        ? Number(element.y)
        : (isFiniteNumber(element.cy) && isFiniteNumber(element.ry) ? Number(element.cy) - Number(element.ry) : 0);

    if (nextType === 'ellipse' && isFiniteNumber(element.cx) && isFiniteNumber(element.cy) && isFiniteNumber(element.rx) && isFiniteNumber(element.ry)) {
        element.width = Number(element.rx) * 2;
        element.height = Number(element.ry) * 2;
    }

    if (nextType === 'text') {
        element.text = String(element.text || '').trim();
        if (!element.text) {
            return null;
        }
        element.fontSize = isFiniteNumber(element.fontSize) ? Number(element.fontSize) : 20;
        element.strokeColor = hasNonEmptyString(element.strokeColor)
            ? element.strokeColor
            : (hasNonEmptyString(element.color) ? element.color : '#000000');
        return element;
    }

    if (nextType === 'line' || nextType === 'arrow' || nextType === 'freedraw') {
        const baseX = element.x;
        const baseY = element.y;
        const normalizedPoints = normalizeExcalidrawPoints(element.points, baseX, baseY);
        if (!normalizedPoints || normalizedPoints.length === 0) {
            return null;
        }
        if ((nextType === 'line' || nextType === 'arrow') && normalizedPoints.length < 2) {
            return null;
        }
        element.points = normalizedPoints;
        return element;
    }

    if (!isFiniteNumber(element.width) || !isFiniteNumber(element.height)) {
        return null;
    }
    element.width = Number(element.width);
    element.height = Number(element.height);
    return element;
};

export const normalizeExcalidrawScene = (diagramScene) => {
    if (diagramScene == null || diagramScene === '') {
        return null;
    }

    let parsedScene = diagramScene;
    if (typeof parsedScene === 'string') {
        try {
            parsedScene = JSON.parse(parsedScene);
        } catch (error) {
            return null;
        }
    }

    // Generated authoring payloads may send a bare elements array as a compact scene subset.
    if (Array.isArray(parsedScene)) {
        parsedScene = { elements: parsedScene };
    }

    if (!isPlainObject(parsedScene) || !Array.isArray(parsedScene.elements)) {
        return null;
    }

    const normalizedElements = parsedScene.elements
        .map((element, index) => normalizeSceneElement(element, index))
        .filter(Boolean);

    if (normalizedElements.length === 0 && parsedScene.elements.length > 0) {
        return null;
    }

    return {
        ...createEmptyExcalidrawScene(),
        ...deepCloneJsonValue(parsedScene),
        elements: normalizedElements,
        appState: buildRuntimeExcalidrawAppState(parsedScene.appState),
        files: isPlainObject(parsedScene.files) ? deepCloneJsonValue(parsedScene.files) : {},
    };
};

export const buildStoredExcalidrawScene = ({ elements = [], appState = {}, files = {} } = {}) => ({
    ...createEmptyExcalidrawScene(),
    elements: Array.isArray(elements) ? deepCloneJsonValue(elements) : [],
    appState: sanitizeStoredExcalidrawAppState(appState),
    files: isPlainObject(files) ? deepCloneJsonValue(files) : {},
});

export const normalizeQuestionDiagram = (value = {}) => {
    const diagramFormat = normalizeDiagramFormat(value.diagramFormat || value.diagram_format);
    const diagramCode = hasDiagramText(value.diagramCode ?? value.diagram_code)
        ? String(value.diagramCode ?? value.diagram_code).trim()
        : null;
    const diagramScene = normalizeExcalidrawScene(value.diagramScene ?? value.diagram_scene);

    const resolvedFormat = diagramFormat === DIAGRAM_FORMATS.NONE
        ? (diagramScene ? DIAGRAM_FORMATS.EXCALIDRAW : diagramCode ? DIAGRAM_FORMATS.SVG : DIAGRAM_FORMATS.NONE)
        : diagramFormat || (diagramScene ? DIAGRAM_FORMATS.EXCALIDRAW : diagramCode ? DIAGRAM_FORMATS.SVG : DIAGRAM_FORMATS.NONE);

    if (resolvedFormat === DIAGRAM_FORMATS.EXCALIDRAW && diagramScene) {
        return {
            diagramFormat: DIAGRAM_FORMATS.EXCALIDRAW,
            diagramCode: null,
            diagramScene,
            hasDiagram: true,
            isSvg: false,
            isExcalidraw: true,
        };
    }

    if (resolvedFormat === DIAGRAM_FORMATS.SVG && diagramCode) {
        return {
            diagramFormat: DIAGRAM_FORMATS.SVG,
            diagramCode,
            diagramScene: null,
            hasDiagram: true,
            isSvg: true,
            isExcalidraw: false,
        };
    }

    return {
        diagramFormat: DIAGRAM_FORMATS.NONE,
        diagramCode: null,
        diagramScene: null,
        hasDiagram: false,
        isSvg: false,
        isExcalidraw: false,
    };
};

export const buildQuestionDiagramPayload = (value = {}) => {
    const normalized = normalizeQuestionDiagram(value);
    return {
        diagramFormat: normalized.diagramFormat,
        diagramCode: normalized.diagramFormat === DIAGRAM_FORMATS.SVG ? normalized.diagramCode : null,
        diagramScene: normalized.diagramFormat === DIAGRAM_FORMATS.EXCALIDRAW ? normalized.diagramScene : null,
    };
};

export const buildGeneratedDiagramPayload = (value = {}) => ({
    ...buildQuestionDiagramPayload(value),
    diagram_code: hasNonEmptyString(value.diagram_code) ? String(value.diagram_code).trim() : null,
    diagram_format: normalizeQuestionDiagram(value).diagramFormat,
    diagram_scene: normalizeQuestionDiagram(value).diagramScene,
});

export const hasQuestionDiagram = (value = {}) => normalizeQuestionDiagram(value).hasDiagram;

export const getDiagramSceneBounds = (diagramScene) => {
    const normalizedScene = normalizeExcalidrawScene(diagramScene);
    if (!normalizedScene || normalizedScene.elements.length === 0) {
        return { width: 0, height: 0 };
    }

    const visibleElements = normalizedScene.elements.filter((element) => !element?.isDeleted);
    if (visibleElements.length === 0) {
        return { width: 0, height: 0 };
    }

    let left = Number.POSITIVE_INFINITY;
    let top = Number.POSITIVE_INFINITY;
    let right = Number.NEGATIVE_INFINITY;
    let bottom = Number.NEGATIVE_INFINITY;

    visibleElements.forEach((element) => {
        const x = Number(element?.x) || 0;
        const y = Number(element?.y) || 0;
        const width = Math.abs(Number(element?.width) || 0);
        const height = Math.abs(Number(element?.height) || 0);

        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x + width);
        bottom = Math.max(bottom, y + height);
    });

    return {
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
    };
};
